import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { motion } from "framer-motion";
import { Medal, Crown, UserCheck, Coins, Lightning, Trophy, Sparkle, Lock, Target as TargetIcon, PencilSimple, Check, X as XIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const BADGE_ICONS = {
  rising_star: Lightning,
  closer: Medal,
  hustler: UserCheck,
  six_figure: Coins,
  legend: Crown,
};

const BADGE_COLORS = {
  rising_star: "#2563eb",
  closer: "#2563eb",
  hustler: "#fb7185",
  six_figure: "#c084fc",
  legend: "#09090b",
};

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function ProgressBar({ pct, qualified }) {
  const safePct = Math.max(0, Math.min(100, pct));
  return (
    <div className="h-3 border-2 border-black bg-white relative overflow-hidden">
      <div className="h-full" style={{ width: `${safePct}%`, background: qualified ? "#22c55e" : "#2563eb" }} />
    </div>
  );
}

function AwardCard({ award, isAdmin, onOverride }) {
  const p = award.progress || {};
  // Build progress sub-rows depending on award type
  const rows = [];
  if (award.key === "rising_star_award") {
    rows.push({ label: "Paid Cases", value: p.cases_paid, target: p.cases_target, kind: "count" });
    rows.push({ label: "Paid Premium (Life/Disability/LTC)", value: p.premium, target: p.premium_target, kind: "money" });
  } else if (award.key === "leaders_tier1") {
    rows.push({ label: "Paid Life Premium", value: p.life_premium, target: p.life_premium_target, kind: "money" });
    rows.push({ label: "Paid Cases", value: p.cases_paid, target: p.cases_target, kind: "count" });
  } else if (award.key === "leaders_tier2") {
    rows.push({ label: "Paid GDC", value: p.gdc, target: p.gdc_target, kind: "money" });
  }

  const ineligible = award.eligible === false;
  const closed = award.window_closed === true && !award.qualified;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className={`brut p-5 bg-white relative ${ineligible ? "opacity-60" : ""}`}
      data-testid={`award-card-${award.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Trophy weight="fill" className="text-[#fbbf24]" size={20} />
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
              {award.key === "rising_star_award" ? "First 90 Days" : "Leader's Conference"}
            </div>
          </div>
          <div className="font-display font-black text-2xl tracking-tighter leading-tight">{award.name}</div>
          <p className="text-sm text-zinc-600 mt-1">{award.description}</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {award.qualified ? (
            <div className="inline-flex items-center gap-1 px-2 py-1 bg-[#22c55e] text-white border-2 border-black text-[10px] font-black uppercase tracking-widest" data-testid={`award-qualified-${award.key}`}>
              <Sparkle weight="fill" size={12} /> Qualified
            </div>
          ) : ineligible ? (
            <div className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 border-2 border-black text-[10px] font-black uppercase tracking-widest text-zinc-500">
              <Lock weight="bold" size={12} /> Not Eligible
            </div>
          ) : closed ? (
            <div className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 border-2 border-black text-[10px] font-black uppercase tracking-widest text-zinc-500">
              Window Closed
            </div>
          ) : (
            <div className="px-2 py-1 bg-[#fde68a] border-2 border-black text-[10px] font-black uppercase tracking-widest">In Progress</div>
          )}
          {award.overridden && <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Admin override</div>}
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {rows.map((r) => {
          const pct = r.target ? Math.round((r.value / r.target) * 100) : 0;
          return (
            <div key={r.label}>
              <div className="flex items-center justify-between text-xs font-bold uppercase tracking-widest text-zinc-600 mb-1">
                <span>{r.label}</span>
                <span>
                  {r.kind === "money" ? `${fmt$(r.value)} / ${fmt$(r.target)}` : `${r.value || 0} / ${r.target}`}
                  <span className="ml-2 text-zinc-400">{pct}%</span>
                </span>
              </div>
              <ProgressBar pct={pct} qualified={award.qualified} />
            </div>
          );
        })}
      </div>

      {isAdmin && (
        <div className="mt-4 pt-3 border-t-2 border-black flex items-center justify-end gap-2">
          <Button
            onClick={() => onOverride(award.key, !award.qualified)}
            className="rounded-none h-8 px-3 bg-white text-black border-2 border-black hover:bg-[#fde68a] text-[10px] font-bold uppercase tracking-widest"
            data-testid={`override-${award.key}`}
          >
            {award.qualified ? (award.overridden ? "Un-qualify" : "Override → Qualify") : "Mark Qualified"}
          </Button>
        </div>
      )}
    </motion.div>
  );
}

export default function Badges() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [badges, setBadges] = useState([]);
  const [awardsResp, setAwardsResp] = useState(null);
  const [editingGoalKey, setEditingGoalKey] = useState(null); // 'annual_paid_premium_goal' | 'annual_annuity_deposit_goal' | 'annual_investment_deposit_goal'
  const [goalDraft, setGoalDraft] = useState("");
  const [savingGoal, setSavingGoal] = useState(false);

  const load = useCallback(async () => {
    const [s, a] = await Promise.all([
      api.get("/stats/me"),
      api.get("/awards/me"),
    ]);
    setBadges(s.data.badges || []);
    setAwardsResp(a.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveGoal = async (goalKey) => {
    setSavingGoal(true);
    try {
      const v = parseFloat(goalDraft);
      if (Number.isNaN(v) || v < 0) { toast.error("Enter a non-negative number"); return; }
      await api.patch(`/users/${user.id}/profile`, { [goalKey]: v });
      toast.success("Annual goal saved");
      setEditingGoalKey(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save goal");
    } finally {
      setSavingGoal(false);
    }
  };

  const handleOverride = async (awardKey, qualify) => {
    try {
      if (qualify) {
        await api.post("/awards/override", {
          agent_id: awardsResp.agent_id, award_key: awardKey, qualified: true,
        });
        toast.success("Award marked as qualified");
      } else {
        // If currently auto-qualified but no override, set override to false. Otherwise clear.
        const award = awardsResp.awards.find((a) => a.key === awardKey);
        if (award?.overridden) {
          await api.delete(`/awards/override?agent_id=${awardsResp.agent_id}&award_key=${awardKey}`);
          toast.success("Override cleared");
        } else {
          await api.post("/awards/override", {
            agent_id: awardsResp.agent_id, award_key: awardKey, qualified: false,
          });
          toast.success("Marked as not qualified");
        }
      }
      load();
    } catch {
      toast.error("Override failed");
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">Achievements</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-1">Leader's Conference &amp; Badges</h1>
        <p className="text-zinc-600 mt-2">CFN milestones + everyday point tiers. Stack 'em up.</p>
      </div>

      {/* Personal Annual Goals (Paid Premium · Annuity Deposits · Investment Deposits) */}
      {Array.isArray(awardsResp?.personal_goals) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="personal-goals">
          {awardsResp.personal_goals.map((g) => {
            const pct = g.goal ? Math.round((g.ytd / g.goal) * 100) : 0;
            const accent =
              g.key === "annual_annuity_deposit_goal" ? "#a855f7" :
              g.key === "annual_investment_deposit_goal" ? "#f97316" :
              "#2563eb";
            const ytdLabel =
              g.key === "annual_annuity_deposit_goal" ? "YTD Annuity Deposits" :
              g.key === "annual_investment_deposit_goal" ? "YTD Investment Deposits" :
              "YTD Paid Premium";
            const editing = editingGoalKey === g.key;
            return (
              <div key={g.key} className="brut p-5 bg-white" data-testid={`personal-goal-card-${g.key}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="border-2 border-black p-2 text-white shrink-0" style={{ background: accent }}>
                      <TargetIcon size={18} weight="bold" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Personal Goal · {g.year}</div>
                      <div className="font-display font-black text-lg tracking-tight leading-tight">{g.name}</div>
                      <p className="text-xs text-zinc-600 mt-1">{g.description}</p>
                    </div>
                  </div>
                  {!editing ? (
                    <button
                      onClick={() => { setGoalDraft(g.goal ? String(g.goal) : ""); setEditingGoalKey(g.key); }}
                      className="inline-flex items-center gap-1 border-2 border-black px-2.5 py-1 bg-white hover:bg-[#fde68a] font-bold uppercase text-[10px] tracking-widest shrink-0"
                      data-testid={`edit-goal-btn-${g.key}`}
                    >
                      <PencilSimple size={12} weight="bold" /> {g.goal ? "Edit" : "Set"}
                    </button>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Input
                        type="number" min="0" step="1000"
                        value={goalDraft}
                        onChange={(e) => setGoalDraft(e.target.value)}
                        placeholder="e.g. 75000"
                        className="border-2 border-black rounded-none h-9 focus-visible:ring-0 w-28"
                        data-testid={`goal-input-${g.key}`}
                      />
                      <Button onClick={() => saveGoal(g.key)} disabled={savingGoal} className="rounded-none h-9 px-2 bg-[#22c55e] text-white border-2 border-black hover:bg-[#16a34a]" data-testid={`save-goal-btn-${g.key}`}>
                        <Check size={14} weight="bold" />
                      </Button>
                      <Button onClick={() => setEditingGoalKey(null)} className="rounded-none h-9 px-2 bg-white text-black border-2 border-black hover:bg-zinc-100" data-testid={`cancel-goal-btn-${g.key}`}>
                        <XIcon size={14} weight="bold" />
                      </Button>
                    </div>
                  )}
                </div>
                {g.goal > 0 ? (
                  <div className="mt-4">
                    <div className="flex items-end justify-between text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-1 gap-2">
                      <span className="truncate">{ytdLabel}</span>
                      <span className="shrink-0">
                        {fmt$(g.ytd)} / {fmt$(g.goal)}
                        <span className="ml-2 text-zinc-400">{pct}%</span>
                      </span>
                    </div>
                    <div className="h-3 border-2 border-black bg-white overflow-hidden">
                      <div className="h-full" style={{ width: `${Math.min(100, pct)}%`, background: g.qualified ? "#22c55e" : accent }} />
                    </div>
                    {g.qualified && <div className="mt-2 inline-block text-[10px] font-black uppercase bg-[#22c55e] text-white border-2 border-black px-1.5 py-0.5">Goal Hit</div>}
                  </div>
                ) : (
                  <div className="mt-4 text-xs text-zinc-500">Set your goal to start tracking your pace.</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* CFN Awards */}
      {awardsResp && (
        <div>
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">CFN Awards</div>
              <div className="font-display font-black text-2xl tracking-tight">Real-World Milestones</div>
            </div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              CFN start: {awardsResp.cfn_start_date || "—"} · {awardsResp.years_with_cfn} yrs
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {awardsResp.awards.map((a) => (
              <AwardCard key={a.key} award={a} isAdmin={isAdmin} onOverride={handleOverride} />
            ))}
          </div>
        </div>
      )}

      {/* Standard badge grid */}
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Point Milestones</div>
        <div className="font-display font-black text-2xl tracking-tight mb-3">Badges</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {badges.map((b, i) => {
            const Icon = BADGE_ICONS[b.key] || Medal;
            const color = BADGE_COLORS[b.key] || "#2563eb";
            const textColor = color === "#09090b" ? "#fff" : "#09090b";
            return (
              <motion.div
                key={b.key}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className={`p-6 border-2 ${b.earned ? "border-black bg-white shadow-[4px_4px_0_0_#09090b]" : "border-dashed border-zinc-400 bg-zinc-50"}`}
                data-testid={`badge-${b.key}`}
              >
                <div className={`w-16 h-16 border-2 ${b.earned ? "border-black" : "border-zinc-400 grayscale opacity-40"} flex items-center justify-center mb-4`} style={{ background: b.earned ? color : "#e4e4e7" }}>
                  <Icon size={32} weight="duotone" color={textColor} />
                </div>
                <div className={`font-display font-black text-xl tracking-tight ${b.earned ? "" : "text-zinc-400"}`}>{b.name}</div>
                <div className={`text-sm mt-1 ${b.earned ? "text-zinc-600" : "text-zinc-400"}`}>{b.description}</div>
                <div className="mt-4 h-2 border-2 border-black">
                  <div className="h-full bg-[#09090b]" style={{ width: `${Math.round((b.progress || 0) * 100)}%` }} />
                </div>
                <div className="flex justify-between mt-2 text-xs font-bold uppercase tracking-widest text-zinc-500">
                  <span>{b.earned ? "EARNED" : "LOCKED"}</span>
                  <span>{Math.round((b.progress || 0) * 100)}%</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
